// ABOUTME: Shared layout shell for authentication pages (login, forgot password).
// ABOUTME: Loads brand by subdomain, provides brand + isOnline via outlet context to child routes.
import { Box, ButtonBase, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { setThemeMode } from "../../newapi/global/settingsSlice";
import { tokens } from "../../theme";
import { localeSettings } from "../../utils/locales";
import useBranding from "./branding/useBranding";
import NightsStayIcon from '@mui/icons-material/NightsStay';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import NightsStayOutlinedIcon from '@mui/icons-material/NightsStayOutlined';
import { ReactComponent as LogoBlackBlue } from "../../styles/svg/logoblackblue.svg";
import { ReactComponent as LogoWhiteLightBlue } from "../../styles/svg/logowhitelightblue.svg";
import { ReactComponent as OnTheRight } from "../../styles/svg/ontheirght.svg";
import { ReactComponent as StockhubLogoBlack } from "../../styles/svg/stockhub-logo-black.svg";
import { ReactComponent as StockhubLogoWhite } from "../../styles/svg/stockhub-logo-white.svg";
import Heading from "../../components/theme/text/Heading";
import MPOChooseLanguage from "../../components/global/ModalsPopover/MPOChooseLanguage";

const AuthLayout = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();

    const { brandSlug } = useParams();
    const { brand, isLoading, fromCache } = useBranding();

    const language = useSelector((state) => state.settings.language);
    const themeMode = useSelector((state) => state.settings.themeMode);

    const [isOnline, setIsOnline] = useState(navigator.onLine);

    useEffect(() => {
        const onOnline = () => setIsOnline(true);
        const onOffline = () => setIsOnline(false);

        window.addEventListener("online", onOnline);
        window.addEventListener("offline", onOffline);

        return () => {
            window.removeEventListener("online", onOnline);
            window.removeEventListener("offline", onOffline);
        };
    }, []);

    const [languageAnchorEl, setLanguageAnchorEl] = useState(null);
    const languagePopoverOpen = Boolean(languageAnchorEl);

    const themeKey = theme.palette.mode === "dark" ? "dark" : "light";

    // When branded: use brand logo (or nothing if null), brand side image (or colored box if null)
    // When not branded: use default StockHub logo and OnTheRight
    const isBranded = !!brand;
    const Logo = isBranded ? brand.logo[themeKey] : theme.palette.mode === "dark" ? LogoWhiteLightBlue : LogoBlackBlue;
    const SideImage = isBranded ? brand.sideImage[themeKey] : OnTheRight;

    return (
        <>
        <AnimatePresence>
            {isLoading && (
                <motion.div
                    key="auth-splash"
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: theme.palette.mode === "dark" ? colors.grey["200"] : colors.grey["0"],
                        zIndex: 2000,
                    }}
                />
            )}
        </AnimatePresence>
        {!isLoading &&
        <motion.div
            key="auth-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            style={{ width: "100%", height: "100%" }}
        >
            <Box
                sx={{
                    width: 1,
                    height: 1,
                    display: "flex",
                    "@media screen and (max-width: 47.9375em)": {
                        flexDirection: "column",
                        overflow:"hidden",
                        position: "relative",
                    }
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        height: 1,
                        "@media screen and (max-width: 47.9375em)": {
                            height: "60vh",
                            flex: "none",
                        },
                        "@media screen and (max-height: 700px)": {
                            height: "70vh",
                            flex: "none",
                        },
                    }}
                >
                    <Box
                        sx={{
                            px: 8,
                            pt: 6,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            "& .logo": {
                                height: 60,
                                maxWidth: 300,
                                objectFit: "contain",
                            },
                            "@media screen and (max-width: 47.9475em)" : {
                                justifyContent:'center',
                                pt:4,
                                "& .logo": {
                                    height:32,
                                    maxWidth: 200,
                                },
                            }
                        }}
                    >
                        {Logo
                            ? (isBranded
                                ? <img className="logo" src={Logo} alt="logo" />
                                : <Logo className="logo" />)
                            : (isBranded && brand.name
                                ? <Heading>{brand.name}</Heading>
                                : <Box />)
                        }
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                "@media screen and (max-width: 47.9475em)" : {
                                    display:"none",
                                }
                            }}
                        >
                            <ButtonBase
                                aria-label={t('language')}
                                aria-describedby={languagePopoverOpen ? 'auth-language-popover' : undefined}
                                onClick={(event) => setLanguageAnchorEl(event.currentTarget)}
                                sx={{
                                    height: 40,
                                    borderRadius: 5,
                                    background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                    boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                                    minWidth: 150,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    gap: 1,
                                    px: 0.5,
                                    pr: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        minWidth: 32,
                                        borderRadius: "50%",
                                        overflow: "hidden",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        "& svg": {
                                            transform: "scale(1.4)",
                                        }
                                    }}
                                >
                                    {localeSettings[language]?.flag}
                                </Box>
                                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                                    {localeSettings[language]?.language}
                                </Typography>
                            </ButtonBase>
                            <MPOChooseLanguage
                                id={languagePopoverOpen ? 'auth-language-popover' : undefined}
                                open={languagePopoverOpen}
                                anchorEl={languageAnchorEl}
                                onClose={() => setLanguageAnchorEl(null)}
                            />
                            <Box
                                sx={{
                                    height: 40,
                                    borderRadius: 5,
                                    background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                    boxShadow: theme.palette.mode === "dark" ? null : `0px 2px 4px ${colors.grey[200]}`,
                                    display: "flex",
                                    alignItems: "center",
                                    overflow: "hidden",
                                    p: 0.5,
                                }}
                            >
                                <ButtonBase
                                    onClick={() => dispatch(setThemeMode("light"))}
                                    sx={{
                                        height: 32,
                                        flex: 1,
                                        px: 2,
                                        fontSize: 14,
                                        fontWeight: 500,
                                        borderRadius: 4,
                                        background: themeMode === "light"
                                            ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100])
                                            : "transparent",
                                        color: themeMode === "light" ? colors.txt["primary"] : colors.txt["secondary"],
                                    }}
                                >
                                    <WbSunnyIcon fontSize="small"/>
                                    {/* {t('light')} */}
                                </ButtonBase>
                                <ButtonBase
                                    onClick={() => dispatch(setThemeMode("dark"))}
                                    sx={{
                                        height: 32,
                                        flex: 1,
                                        px: 2,
                                        fontSize: 14,
                                        fontWeight: 500,
                                        borderRadius: 4,
                                        background: themeMode === "dark"
                                            ? (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100])
                                            : "transparent",
                                        color: themeMode === "dark" ? colors.txt["primary"] : colors.txt["secondary"],
                                    }}
                                >
                                    <NightsStayIcon fontSize="small"/>
                                    {/* {t('dark')} */}
                                </ButtonBase>
                            </Box>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            mt: "auto",
                            "@media screen and (max-width: 47.9375em)": {
                                mt: "auto",
                                mb: "auto",
                            },
                        }}
                    >
                        {!isOnline &&
                            <Box
                                sx={{
                                    width: 514,
                                    textAlign: 'center',
                                    px: 2,
                                    height: 32,
                                    lineHeight: "32px",
                                    background: colors.red[400],
                                    borderRadius: 4,
                                    color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                    fontSize: 14,
                                    fontWeight: 500,
                                    fontFamily: "Roboto, sans-serif",
                                    mb: 3,
                                    "@media screen and (max-width: 47.9375em)": {
                                        minWidth: 0,
                                        borderTopRightRadius: 0,
                                        borderBottomRightRadius: 0,
                                    }
                                }}
                            >
                                {t("you_are_offline")}
                            </Box>
                        }
                            <motion.div
                                key={location.pathname}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3 }}
                            >
                                <Outlet context={{ isOnline, brand, brandSlug }} />
                            </motion.div>
                    </Box>
                    <Box
                        sx={{
                            mt: "auto",
                            pb: 6,
                            px: 5,
                            height: 44,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            opacity:0.3,
                            "@media screen and (max-width: 47.9375em)": {
                                display: "none",
                            },
                        }}
                    >
                        {theme.palette.mode === "dark"
                            ? <StockhubLogoWhite style={{ height: 42, width: "auto" }} />
                            : <StockhubLogoBlack style={{ height: 42, width: "auto" }} />
                        }
                    </Box>
                </Box>
                {/* Right side: SVG image if available, or brand-colored box for branded logins, or default OnTheRight */}
                {SideImage ? (
                    isBranded ? (
                        <Box
                            sx={{
                                height: "100%",
                                maxWidth:"35%",
                                aspectRatio: "671 / 1080",
                                flexShrink: 0,
                                position: "relative",
                                "& img": {
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                },
                                "@media screen and (max-width: 47.9375em)": {
                                    height: "40vh",
                                    aspectRatio: "unset",
                                    width: 1,
                                    flexShrink: 1,
                                    maxWidth:"unset",
                                },
                                "@media screen and (max-height: 700px)": {
                                    height: "30vh",
                                    aspectRatio: "unset",
                                    width: 1,
                                    flexShrink: 1,
                                    maxWidth:"unset",
                                },
                            }}
                        >
                            <img src={SideImage} alt="side" />
                            <Box sx={{ position: "fixed", bottom: 12, right: 16, display: "none", "@media screen and (max-width: 47.9375em)": { display: "block" } }}>
                                {theme.palette.mode === "dark"
                                    ? <StockhubLogoWhite style={{ height: 32, width: "auto" }} />
                                    : <StockhubLogoBlack style={{ height: 32, width: "auto" }} />
                                }
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                height: "100%",
                                aspectRatio: "671 / 1080",
                                position: "relative",
                                overflow: "hidden",
                                maxWidth:"35%",
                                "& svg": {
                                    height: "100%",
                                    width: "auto",
                                    display: "block",
                                },
                                "@media screen and (max-width: 47.9375em)": {
                                    height: "40vh",
                                    maxWidth:"unset",
                                    aspectRatio: "unset",
                                    width: 1,
                                    "& svg": {
                                        height: "100%",
                                        width: "100%",
                                    },
                                },
                                "@media screen and (max-height: 700px)": {
                                    height: "30vh",
                                    maxWidth:"unset",
                                    aspectRatio: "unset",
                                    width: 1,
                                },
                            }}
                        >
                            <svg width="671" height="1080" viewBox="0 0 671 1080" preserveAspectRatio="xMidYMin slice" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="671" height="1080" fill={colors.primary[400]} fillOpacity="0.1"/>
                                <g filter="url(#bf0)">
                                    <path d="M367.319 219.438L-109.822 -67L-206 -56.9934L309.238 252.584V406.435L792 696L787.004 624.703L367.319 374.539V219.438Z" fill={colors.primary[400]}/>
                                    <path d="M367.319 219.438L-109.822 -67L-206 -56.9934L309.238 252.584V406.435L792 696L787.004 624.703L367.319 374.539V219.438Z"/>
                                </g>
                                <g filter="url(#bf1)">
                                    <path d="M499.319 140.438L22.1777 -146L-74 -135.993L441.238 173.584V327.435L924 617L919.004 545.703L499.319 295.539V140.438Z" fill={colors.primary[400]} fillOpacity="0.5"/>
                                    <path d="M499.319 140.438L22.1777 -146L-74 -135.993L441.238 173.584V327.435L924 617L919.004 545.703L499.319 295.539V140.438Z"/>
                                </g>
                                <g filter="url(#bf2)">
                                    <path d="M631.319 61.4377L154.178 -225L58 -214.993L573.238 94.5844V248.435L1056 538L1051 466.703L631.319 216.539V61.4377Z" fill={colors.primary[400]} fillOpacity="0.2"/>
                                    <path d="M631.319 61.4377L154.178 -225L58 -214.993L573.238 94.5844V248.435L1056 538L1051 466.703L631.319 216.539V61.4377Z"/>
                                </g>
                                <defs>
                                    <filter id="bf0" x="-607.539" y="-467.515" width="1800.1" height="1564.44" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                        <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                                        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                                        <feGaussianBlur stdDeviation="0" result="effect1"/>
                                    </filter>
                                    <filter id="bf1" x="-475.539" y="-546.515" width="1800.1" height="1564.44" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                        <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                                        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                                        <feGaussianBlur stdDeviation="0" result="effect1"/>
                                    </filter>
                                    <filter id="bf2" x="-343.539" y="-625.515" width="1800.1" height="1564.44" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                        <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                                        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                                        <feGaussianBlur stdDeviation="0" result="effect1"/>
                                    </filter>
                                </defs>
                            </svg>
                            <Box sx={{ position: "absolute", bottom: 16, right: 16, display: "none", "@media screen and (max-width: 47.9375em)": { display: "block" } }}>
                                {theme.palette.mode === "dark"
                                    ? <StockhubLogoWhite style={{ height: 32, width: "auto" }} />
                                    : <StockhubLogoBlack style={{ height: 32, width: "auto" }} />
                                }
                            </Box>
                        </Box>
                    )
                    // 14t and 14pro xiaomi
                ) : isBranded && (
                    <Box
                        sx={{
                            height: "100%",
                            aspectRatio: "671 / 1080",
                            position: "relative",
                            overflow: "hidden",
                            maxWidth:"35%",
                            "& svg": {
                                height: "100%",
                                width: "auto",
                                display: "block",
                            },
                            "@media screen and (max-width: 47.9375em)": {
                                height: "40vh",
                                maxWidth:"unset",
                                aspectRatio: "unset",
                                width: 1,
                                "& svg": {
                                    height: "100%",
                                    width: "100%",
                                },
                            },
                            "@media screen and (max-height: 700px)": {
                                height: "30vh",
                                maxWidth:"unset",
                                aspectRatio: "unset",
                                width: 1,
                            },
                        }}
                    >
                        <svg width="671" height="1080" viewBox="0 0 671 1080" preserveAspectRatio="xMidYMin slice" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect width="671" height="1080" fill={brand.color.hex} fillOpacity="0.1"/>
                            <g filter="url(#bf0)">
                                <path d="M367.319 219.438L-109.822 -67L-206 -56.9934L309.238 252.584V406.435L792 696L787.004 624.703L367.319 374.539V219.438Z" fill={brand.color.hex}/>
                                <path d="M367.319 219.438L-109.822 -67L-206 -56.9934L309.238 252.584V406.435L792 696L787.004 624.703L367.319 374.539V219.438Z"/>
                            </g>
                            <g filter="url(#bf1)">
                                <path d="M499.319 140.438L22.1777 -146L-74 -135.993L441.238 173.584V327.435L924 617L919.004 545.703L499.319 295.539V140.438Z" fill={brand.color.hex} fillOpacity="0.5"/>
                                <path d="M499.319 140.438L22.1777 -146L-74 -135.993L441.238 173.584V327.435L924 617L919.004 545.703L499.319 295.539V140.438Z"/>
                            </g>
                            <g filter="url(#bf2)">
                                <path d="M631.319 61.4377L154.178 -225L58 -214.993L573.238 94.5844V248.435L1056 538L1051 466.703L631.319 216.539V61.4377Z" fill={brand.color.hex} fillOpacity="0.2"/>
                                <path d="M631.319 61.4377L154.178 -225L58 -214.993L573.238 94.5844V248.435L1056 538L1051 466.703L631.319 216.539V61.4377Z"/>
                            </g>
                            <defs>
                                <filter id="bf0" x="-607.539" y="-467.515" width="1800.1" height="1564.44" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                                    <feGaussianBlur stdDeviation="0" result="effect1"/>
                                </filter>
                                <filter id="bf1" x="-475.539" y="-546.515" width="1800.1" height="1564.44" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                                    <feGaussianBlur stdDeviation="0" result="effect1"/>
                                </filter>
                                <filter id="bf2" x="-343.539" y="-625.515" width="1800.1" height="1564.44" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
                                    <feGaussianBlur stdDeviation="0" result="effect1"/>
                                </filter>
                            </defs>
                        </svg>
                        <Box sx={{ position: "absolute", bottom: 16, right: 16, display: "none", "@media screen and (max-width: 47.9375em)": { display: "block" } }}>
                            {theme.palette.mode === "dark"
                                ? <StockhubLogoWhite style={{ height: 32, width: "auto" }} />
                                : <StockhubLogoBlack style={{ height: 32, width: "auto" }} />
                            }
                        </Box>
                    </Box>
                )}
            </Box>
        </motion.div>
        }
        </>
    )
}

export default AuthLayout;
